import React, { useEffect, useState, useCallback } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { RUBRIQUE_BY_ID, RUBRIQUES, ROLES_PERSONNE_CONFIANCE } from "../rubriques";
import { Lock, Save, Plus, Trash2, Mail, CheckCircle2, Info } from "lucide-react";

// ---- Helpers structured / repeater ----
function parseStructured(value) {
  if (!value) return { items: [], legacy: "" };
  if (typeof value !== "string") return { items: [], legacy: "" };
  const trimmed = value.trim();
  if (!trimmed) return { items: [], legacy: "" };
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return { items: parsed, legacy: "" };
      if (parsed && typeof parsed === "object") return { items: [], obj: parsed, legacy: "" };
    } catch (_e) {
      return { items: [], legacy: value };
    }
  }
  return { items: [], legacy: value };
}

function serializeItems(items) {
  if (!items || items.length === 0) return "";
  return JSON.stringify(items);
}

function serializeObject(obj) {
  if (!obj) return "";
  const hasValue = Object.values(obj).some((v) => v && String(v).trim());
  if (!hasValue) return "";
  return JSON.stringify(obj);
}

function useDebouncedSave(rubrique, onProgress) {
  const [pending, setPending] = useState({});
  const [savingKeys, setSavingKeys] = useState({});

  const enqueue = (clef, valeur) => {
    setPending((p) => ({ ...p, [clef]: valeur }));
  };

  useEffect(() => {
    const keys = Object.keys(pending);
    if (keys.length === 0) return;
    const t = setTimeout(async () => {
      for (const clef of keys) {
        try {
          setSavingKeys((s) => ({ ...s, [clef]: true }));
          const { data } = await api.post("/carnet/profil", { rubrique, clef, valeur: pending[clef] });
          if (onProgress && typeof data.progression === "number") onProgress(data.progression);
        } catch (_e) {
          // ignore individual errors
        } finally {
          setSavingKeys((s) => ({ ...s, [clef]: false }));
        }
      }
      setPending({});
    }, 600);
    return () => clearTimeout(t);
  }, [pending, rubrique, onProgress]);

  return { enqueue, savingKeys };
}

export default function CarnetSection() {
  const { rubriqueId } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const def = RUBRIQUE_BY_ID[rubriqueId];
  const [carnetData, setCarnetData] = useState(null);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/carnet");
      setCarnetData(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onProgress = (p) => {
    setCarnetData((d) => d ? { ...d, carnet: { ...d.carnet, progression: p } } : d);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const { enqueue, savingKeys } = useDebouncedSave(rubriqueId, onProgress);

  if (!def) return <Navigate to="/carnet/identite" replace />;
  if (!carnetData) return <div>Chargement…</div>;

  const plan = carnetData.plan;
  const allowed = plan.accessible_sections.includes(rubriqueId);

  return (
    <div className="space-y-8">
      {/* Header with progress */}
      <CarnetHeader carnet={carnetData.carnet} planName={plan.name} planId={plan.id} />

      <div>
        <div style={{ color: "var(--auprea-gold)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>
          — Rubrique
        </div>
        <h1 style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }} data-testid="rubrique-title">{def.label}</h1>
        <p style={{ fontSize: 17, color: "rgba(35,38,46,0.7)", marginTop: 8 }}>{def.description}</p>
      </div>

      {!allowed ? (
        <UpsellCard onUpgrade={() => nav("/tarifs")} />
      ) : (
        <div className="auprea-card">
          {savedFlash && (
            <div style={{ position: "fixed", top: 80, right: 24, backgroundColor: "var(--auprea-green)", color: "white", padding: "8px 16px", fontSize: 14, zIndex: 50 }} data-testid="save-flash">
              <CheckCircle2 size={14} style={{ display: "inline", marginRight: 6 }} />
              Sauvegardé
            </div>
          )}
          {error && <div style={{ color: "#b53a3a", marginBottom: 16 }}>{error}</div>}

          <div className="space-y-6">
            {/* Encart conseil dynamique pour PACS / Concubinage */}
            {rubriqueId === "couple" && (() => {
              const statut = (carnetData.profil?.couple?.statut || "").toLowerCase();
              const isPacs = statut.includes("pacs");
              const isConcubinage = statut.includes("concubinage");
              if (!isPacs && !isConcubinage) return null;
              return (
                <div
                  style={{
                    background: "rgba(202,168,106,0.1)",
                    borderLeft: "3px solid var(--auprea-gold)",
                    padding: "16px 20px",
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}
                  data-testid="pacs-warning"
                >
                  <div style={{ fontWeight: 600, color: "var(--auprea-navy)", marginBottom: 6, fontSize: 15 }}>
                    ⚠️ Conseil important — {isPacs ? "Partenaire de PACS" : "Concubinage"}
                  </div>
                  <p style={{ margin: 0, color: "rgba(35,38,46,0.85)" }}>
                    {isPacs
                      ? "Le PACS ne donne aucun droit successoral à votre partenaire : en l'absence de testament, il ou elle n'héritera de rien (et sera soumis à 60 % de droits sur tout legs). "
                      : "Le concubinage n'a aucune existence juridique en matière de succession : sans testament, votre concubin(e) n'a strictement aucun droit sur votre patrimoine. "}
                    <strong>La rédaction d&apos;un testament est indispensable pour protéger votre partenaire.</strong>
                  </p>
                </div>
              );
            })()}
            {def.fields.map((f) => {
              // Champ conditionnel (showIf)
              if (typeof f.showIf === "function" && !f.showIf(carnetData.profil?.[rubriqueId] || {})) {
                return null;
              }
              return (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={carnetData.profil?.[rubriqueId]?.[f.key] || ""}
                  onChange={(val) => {
                    setCarnetData((d) => {
                      const profil = { ...(d.profil || {}) };
                      profil[rubriqueId] = { ...(profil[rubriqueId] || {}), [f.key]: val };
                      return { ...d, profil };
                    });
                    enqueue(f.key, val);
                    // Trigger lead rapatriement quand l'utilisateur choisit "Oui" (souscription accompagnée)
                    if (f.key === "souhait_accompagnement_rapatriement" && /^oui/i.test(val)) {
                      api.post("/lead/rapatriement", { message: "" }).catch(() => {});
                    }
                    // Trigger lead accompagnement souscription assurance obsèques
                    if (f.key === "souhait_accompagnement_obseques" && /^oui/i.test(val)) {
                      api.post("/lead/assurance-obseques", { message: "" }).catch(() => {});
                    }
                    // Trigger lead rachat de crédits
                    if (f.key === "souhait_rachat_credits" && /^oui/i.test(val)) {
                      api.post("/lead/rachat-credits", { message: "" }).catch(() => {});
                    }
                    // Trigger lead mandat de protection future
                    if (f.key === "souhait_accompagnement_mandat" && /contactez-moi/i.test(val)) {
                      api.post("/lead/mandat-protection", { message: "" }).catch(() => {});
                    }
                    // Trigger lead accompagnement rédaction testament (partenaire PACS)
                    if (f.key === "souhait_accompagnement_testament_pacs" && /contactez-moi/i.test(val)) {
                      api.post("/lead/testament-pacs", { message: "" }).catch(() => {});
                    }
                  }}
                  saving={savingKeys[f.key]}
                />
              );
            })}
          </div>

          {def.list === "enfants" && (def.listShowIf ? def.listShowIf(carnetData.profil || {}) : true) && <EnfantsList enfants={carnetData.enfants} reload={load} />}
          {def.list === "biens" && <BiensList biens={carnetData.biens} reload={load} />}
          {def.list === "personnes_confiance" && (
            <PersonnesList personnes={carnetData.personnes_confiance} reload={load} />
          )}
        </div>
      )}

      {/* Bottom nav */}
      <div className="flex justify-between items-center pt-4">
        <PrevNextNav current={rubriqueId} />
      </div>
    </div>
  );
}

function CarnetHeader({ carnet, planName, planId }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-4">
      <div>
        <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Mon carnet</div>
        <div style={{ fontFamily: "Spectral, serif", fontSize: 18, marginTop: 4 }}>
          Forfait <strong>{planName}</strong>
        </div>
      </div>
      <div className="w-full md:w-80" data-testid="progress-bar">
        <div className="flex justify-between text-sm mb-2">
          <span>Progression</span>
          <span style={{ fontWeight: 600 }} data-testid="progression-value">{carnet.progression}%</span>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: `${carnet.progression}%` }} />
        </div>
      </div>
    </div>
  );
}

function PrevNextNav({ current }) {
  const idx = RUBRIQUES.findIndex((r) => r.id === current);
  const prev = idx > 0 ? RUBRIQUES[idx - 1] : null;
  const next = idx < RUBRIQUES.length - 1 ? RUBRIQUES[idx + 1] : null;
  return (
    <>
      {prev ? (
        <a href={`/carnet/${prev.id}`} className="auprea-btn-outline" style={{ padding: "10px 20px", minHeight: "auto" }} data-testid="nav-prev">
          ← {prev.label}
        </a>
      ) : <span />}
      {next && (
        <a href={`/carnet/${next.id}`} className="auprea-btn" style={{ padding: "10px 20px", minHeight: "auto" }} data-testid="nav-next">
          {next.label} →
        </a>
      )}
    </>
  );
}

function FieldRow({ field, value, onChange, saving }) {
  const id = `field-${field.key}`;
  // Type "info" : bloc d'information statique (pas de saisie)
  if (field.type === "info") {
    const isWarning = field.variant === "warning";
    return (
      <div
        style={{
          background: isWarning ? "rgba(181,58,58,0.06)" : "rgba(202,168,106,0.08)",
          borderLeft: `3px solid ${isWarning ? "#b53a3a" : "var(--auprea-gold)"}`,
          padding: "16px 20px",
          borderRadius: 4,
          fontSize: 14,
          lineHeight: 1.65,
          color: "rgba(35,38,46,0.85)",
        }}
        data-testid={`info-${field.key}`}
      >
        {field.title && (
          <div style={{ fontWeight: 600, color: isWarning ? "#8a2a2a" : "var(--auprea-navy)", marginBottom: 8, fontSize: 15 }}>
            {field.title}
          </div>
        )}
        <div dangerouslySetInnerHTML={{ __html: field.content }} />
      </div>
    );
  }
  if (field.type === "repeater") {
    return <RepeaterField field={field} value={value} onChange={onChange} saving={saving} />;
  }
  if (field.type === "structured") {
    return <StructuredField field={field} value={value} onChange={onChange} saving={saving} />;
  }
  return (
    <div>
      <label className="auprea-label" htmlFor={id}>
        {field.label} {saving && <span style={{ fontSize: 11, color: "var(--auprea-gold)", marginLeft: 8, textTransform: "none", letterSpacing: 0 }}>sauvegarde…</span>}
      </label>
      {field.type === "textarea" ? (
        <textarea id={id} className="auprea-input" rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.key}`} />
      ) : field.type === "select" ? (
        <>
          <select id={id} className="auprea-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.key}`}>
            <option value="">— Choisir —</option>
            {/* Legacy compat : si la valeur existante n'est dans aucune option, on la conserve */}
            {value && !field.options.includes(value) && (
              <option value={value}>{`${value} (ancienne saisie)`}</option>
            )}
            {field.options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          {field.helpText && (
            <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)", marginTop: 4, fontStyle: "italic" }} data-testid={`help-${field.key}`}>
              {field.helpText}
            </div>
          )}
        </>
      ) : (
        <>
          <input id={id} type={field.type} className="auprea-input" placeholder={field.placeholder || ""} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.key}`} />
          {field.helpText && (
            <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)", marginTop: 4, fontStyle: "italic" }} data-testid={`help-${field.key}`}>
              {field.helpText}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---- RepeaterField : liste d'items structurés ----
function RepeaterField({ field, value, onChange, saving }) {
  const parsed = parseStructured(value);
  const items = parsed.items || [];
  const legacy = parsed.legacy || "";

  const emptyItem = () => Object.fromEntries(field.itemFields.map((f) => [f.key, ""]));

  const addItem = () => {
    const next = [...items, emptyItem()];
    onChange(serializeItems(next));
  };
  const updateItem = (idx, key, val) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: val } : it));
    onChange(serializeItems(next));
  };
  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(serializeItems(next));
  };

  const itemSummary = (it) => {
    const parts = field.itemFields.map((f) => it[f.key]).filter(Boolean);
    return parts.join(" • ") || `${field.itemLabel || "élément"} (à compléter)`;
  };

  return (
    <div data-testid={`repeater-${field.key}`}>
      <label className="auprea-label">
        {field.label} {saving && <span style={{ fontSize: 11, color: "var(--auprea-gold)", marginLeft: 8, textTransform: "none", letterSpacing: 0 }}>sauvegarde…</span>}
      </label>

      {legacy && (
        <div
          style={{
            background: "rgba(29,58,114,0.06)",
            borderLeft: "3px solid var(--auprea-blue)",
            padding: "12px 14px",
            fontSize: 13,
            color: "rgba(35,38,46,0.8)",
            marginBottom: 12,
            borderRadius: 3,
          }}
          data-testid={`repeater-legacy-${field.key}`}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontWeight: 600, color: "var(--auprea-navy)" }}>
            <Info size={14} /> Votre ancienne saisie
          </div>
          <div style={{ whiteSpace: "pre-wrap", fontStyle: "italic", marginBottom: 6 }}>{legacy}</div>
          <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>
            Recopiez ces informations dans le format structuré ci-dessous puis votre ancienne note disparaîtra automatiquement.
          </div>
        </div>
      )}

      {items.length === 0 && !legacy && (
        <div
          style={{
            fontSize: 13,
            color: "rgba(35,38,46,0.55)",
            background: "rgba(35,38,46,0.03)",
            padding: "12px 14px",
            borderRadius: 3,
            marginBottom: 8,
          }}
        >
          Aucun {field.itemLabel || "élément"} renseigné pour l&apos;instant.
        </div>
      )}

      <div className="space-y-3">
        {items.map((it, idx) => (
          <div
            key={idx}
            style={{
              border: "1px solid rgba(21,41,79,0.12)",
              padding: "14px 14px 6px",
              borderRadius: 4,
              background: "#fff",
            }}
            data-testid={`repeater-item-${field.key}-${idx}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div style={{ fontSize: 13, color: "var(--auprea-navy)", fontWeight: 600 }}>
                {field.itemLabel ? field.itemLabel.charAt(0).toUpperCase() + field.itemLabel.slice(1) : "Élément"} #{idx + 1}
                <span style={{ fontWeight: 400, marginLeft: 8, color: "rgba(35,38,46,0.55)" }}>{itemSummary(it)}</span>
              </div>
              <button
                onClick={() => removeItem(idx)}
                style={{ color: "#b53a3a" }}
                aria-label="Supprimer"
                data-testid={`repeater-del-${field.key}-${idx}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {field.itemFields.map((sf) => (
                <div key={sf.key}>
                  <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {sf.label}
                  </label>
                  {sf.type === "select" ? (
                    <select
                      className="auprea-input"
                      value={it[sf.key] || ""}
                      onChange={(e) => updateItem(idx, sf.key, e.target.value)}
                      data-testid={`repeater-input-${field.key}-${idx}-${sf.key}`}
                    >
                      <option value="">— Choisir —</option>
                      {sf.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={sf.type || "text"}
                      className="auprea-input"
                      placeholder={sf.placeholder || ""}
                      value={it[sf.key] || ""}
                      onChange={(e) => updateItem(idx, sf.key, e.target.value)}
                      data-testid={`repeater-input-${field.key}-${idx}-${sf.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addItem}
        className="auprea-btn-outline mt-3"
        style={{ padding: "8px 14px", minHeight: "auto", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
        data-testid={`repeater-add-${field.key}`}
      >
        <Plus size={14} /> Ajouter un {field.itemLabel || "élément"}
      </button>
    </div>
  );
}

// ---- StructuredField : objet à champs fixes ----
function StructuredField({ field, value, onChange, saving }) {
  const parsed = parseStructured(value);
  const obj = parsed.obj || {};
  const legacy = parsed.legacy || "";

  const update = (key, val) => {
    const next = { ...obj, [key]: val };
    onChange(serializeObject(next));
  };

  return (
    <div data-testid={`structured-${field.key}`}>
      <label className="auprea-label">
        {field.label} {saving && <span style={{ fontSize: 11, color: "var(--auprea-gold)", marginLeft: 8, textTransform: "none", letterSpacing: 0 }}>sauvegarde…</span>}
      </label>

      {legacy && (
        <div
          style={{
            background: "rgba(29,58,114,0.06)",
            borderLeft: "3px solid var(--auprea-blue)",
            padding: "12px 14px",
            fontSize: 13,
            color: "rgba(35,38,46,0.8)",
            marginBottom: 12,
            borderRadius: 3,
          }}
          data-testid={`structured-legacy-${field.key}`}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontWeight: 600, color: "var(--auprea-navy)" }}>
            <Info size={14} /> Votre ancienne saisie
          </div>
          <div style={{ whiteSpace: "pre-wrap", fontStyle: "italic", marginBottom: 6 }}>{legacy}</div>
          <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>
            Recopiez ces informations dans les champs structurés ci-dessous, votre ancienne note disparaîtra automatiquement.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {field.subFields.map((sf) => (
          <div key={sf.key}>
            <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {sf.label}
            </label>
            {sf.type === "select" ? (
              <select
                className="auprea-input"
                value={obj[sf.key] || ""}
                onChange={(e) => update(sf.key, e.target.value)}
                data-testid={`structured-input-${field.key}-${sf.key}`}
              >
                <option value="">— Choisir —</option>
                {sf.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                type={sf.type || "text"}
                className="auprea-input"
                placeholder={sf.placeholder || ""}
                value={obj[sf.key] || ""}
                onChange={(e) => update(sf.key, e.target.value)}
                data-testid={`structured-input-${field.key}-${sf.key}`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function UpsellCard({ onUpgrade }) {
  return (
    <div className="auprea-card" style={{ borderColor: "var(--auprea-gold)", backgroundColor: "#faf4e8" }}>
      <div className="flex items-start gap-4">
        <Lock size={28} color="#caa86a" />
        <div className="flex-1">
          <h3 style={{ fontSize: 22, marginBottom: 8 }}>Rubrique réservée à un forfait supérieur</h3>
          <p style={{ marginBottom: 20, lineHeight: 1.6 }}>
            Pour accéder à cette rubrique, passez au forfait Essentiel ou Confort.
            Vous débloquez l&apos;ensemble du carnet, le coffre-fort, la synthèse et les partages.
          </p>
          <button className="auprea-btn-gold" onClick={onUpgrade} data-testid="btn-upgrade">
            Voir les forfaits
          </button>
        </div>
      </div>
    </div>
  );
}

function EnfantsList({ enfants, reload }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ prenom: "", nom: "", date_naissance: "", filiation: "", telephone: "", email: "", adresse_postale: "" });

  // Calcul automatique majorité (≥ 18 ans à la date du jour)
  const isMajeur = (dob) => {
    if (!dob) return false;
    try {
      const d = new Date(dob);
      if (isNaN(d.getTime())) return false;
      const today = new Date();
      const age = today.getFullYear() - d.getFullYear() -
        ((today.getMonth() < d.getMonth() || (today.getMonth() === d.getMonth() && today.getDate() < d.getDate())) ? 1 : 0);
      return age >= 18;
    } catch (_e) { return false; }
  };
  const formMajeur = isMajeur(form.date_naissance);

  const add = async () => {
    if (!form.prenom) return;
    // Persister le statut majeur calculé pour compat back
    const payload = { ...form, majeur: formMajeur ? "Oui" : "Non" };
    await api.post("/carnet/enfants", payload);
    setForm({ prenom: "", nom: "", date_naissance: "", filiation: "", telephone: "", email: "", adresse_postale: "" });
    setAdding(false);
    reload();
  };
  const del = async (id) => {
    await api.delete(`/carnet/enfants/${id}`);
    reload();
  };

  return (
    <div className="mt-10 pt-8 border-t" style={{ borderColor: "rgba(21,41,79,0.1)" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 style={{ fontSize: 20 }}>Mes enfants ({enfants.length})</h3>
        <button onClick={() => setAdding(true)} className="auprea-btn" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 14 }} data-testid="btn-add-enfant">
          <Plus size={16} /> Ajouter un enfant
        </button>
      </div>
      {enfants.map((e) => {
        // Recalcul dynamique de la majorité côté affichage
        const adult = isMajeur(e.date_naissance) || (e.majeur || "").toLowerCase().startsWith("oui");
        return (
          <div key={e.id} className="py-3 border-b" style={{ borderColor: "rgba(21,41,79,0.08)" }} data-testid={`enfant-row-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Identité</div>
                  <div style={{ fontWeight: 600 }}>{[e.prenom, e.nom].filter(Boolean).join(" ") || "-"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Date de naissance</div>
                  <div>{e.date_naissance || "-"} {adult && <span style={{ color: "var(--auprea-green)", fontSize: 11, marginLeft: 4 }}>(majeur)</span>}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Filiation</div>
                  <div>{e.filiation || "-"}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {adult ? "Coordonnées (si connues)" : "Mineur"}
                  </div>
                  <div style={{ color: "rgba(35,38,46,0.75)" }}>
                    {adult ? (
                      <>
                        {e.telephone && <div>{e.telephone}</div>}
                        {e.email && <div>{e.email}</div>}
                        {e.adresse_postale && <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{e.adresse_postale}</div>}
                        {!e.telephone && !e.email && !e.adresse_postale && <span style={{ color: "rgba(35,38,46,0.5)" }}>-</span>}
                      </>
                    ) : <span style={{ color: "rgba(35,38,46,0.5)" }}>-</span>}
                  </div>
                </div>
              </div>
              <button onClick={() => del(e.id)} style={{ color: "#b53a3a" }} data-testid={`btn-del-enfant-${e.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        );
      })}
      {adding && (
        <div className="mt-4 p-4" style={{ background: "rgba(243,239,230,0.4)", border: "1px solid rgba(21,41,79,0.1)" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Prénom *</label>
              <input className="auprea-input" placeholder="Prénom" value={form.prenom} onChange={(ev) => setForm({ ...form, prenom: ev.target.value })} data-testid="input-enfant-prenom" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Nom de famille</label>
              <input className="auprea-input" placeholder="Nom de famille" value={form.nom} onChange={(ev) => setForm({ ...form, nom: ev.target.value })} data-testid="input-enfant-nom" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Date de naissance</label>
              <input type="date" className="auprea-input" value={form.date_naissance}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(ev) => setForm({ ...form, date_naissance: ev.target.value })} data-testid="input-enfant-date" />
              {form.date_naissance && (
                <div style={{ fontSize: 11, color: formMajeur ? "var(--auprea-green)" : "rgba(35,38,46,0.55)", marginTop: 4, fontStyle: "italic" }}>
                  {formMajeur ? "✓ Majeur — coordonnées facultatives à renseigner ci-dessous" : "Mineur — pas de coordonnées à renseigner"}
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Filiation</label>
              <input className="auprea-input" placeholder="Ex. commun, du premier mariage…" value={form.filiation} onChange={(ev) => setForm({ ...form, filiation: ev.target.value })} data-testid="input-enfant-filiation" />
            </div>
            {formMajeur && (
              <>
                <div className="md:col-span-2">
                  <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Adresse postale (si connue)</label>
                  <input className="auprea-input" placeholder="N°, rue, code postal, ville" value={form.adresse_postale} onChange={(ev) => setForm({ ...form, adresse_postale: ev.target.value })} data-testid="input-enfant-adresse" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Numéro de portable (si connu)</label>
                  <input className="auprea-input" placeholder="06 12 34 56 78" value={form.telephone} onChange={(ev) => setForm({ ...form, telephone: ev.target.value })} data-testid="input-enfant-tel" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Adresse email (si connue)</label>
                  <input type="email" className="auprea-input" placeholder="prenom@email.fr" value={form.email} onChange={(ev) => setForm({ ...form, email: ev.target.value })} data-testid="input-enfant-email" />
                </div>
              </>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={add} className="auprea-btn" data-testid="btn-save-enfant">
              <Save size={14} style={{ display: "inline", marginRight: 6 }} /> Enregistrer
            </button>
            <button onClick={() => setAdding(false)} className="auprea-btn-outline">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BiensList({ biens, reload }) {
  const [adding, setAdding] = useState(false);
  const initForm = {
    type_bien: "", adresse_complete: "", code_postal: "", ville: "",
    valeur: 0, detention: "", credit_en_cours: "", credit_montant_restant: 0,
    type_bail: "", remarque_locataire: "",
    numero_cadastre: "", commune: "",
    type: "", localisation: "",
  };
  const [form, setForm] = useState(initForm);
  const [accompagnementSaved, setAccompagnementSaved] = useState(false);

  const isLocatif = /locatif/i.test(form.type_bien);
  const isFoncier = /foncier|terrain/i.test(form.type_bien);
  const hasCredit = /oui/i.test(form.credit_en_cours);

  const add = async () => {
    if (!form.type_bien) return;
    const label = form.type_bien;
    const localisation = isFoncier
      ? `${form.commune}${form.numero_cadastre ? " (cadastre " + form.numero_cadastre + ")" : ""}`
      : `${form.adresse_complete} ${form.code_postal} ${form.ville}`.trim();
    await api.post("/carnet/biens", {
      ...form,
      type: label, // Legacy field kept as human label
      valeur: parseFloat(form.valeur) || 0,
      credit_montant_restant: parseFloat(form.credit_montant_restant) || 0,
      localisation,
    });
    setForm(initForm);
    setAdding(false);
    reload();
  };
  const del = async (id) => { await api.delete(`/carnet/biens/${id}`); reload(); };

  const requestAccompagnement = async () => {
    setAccompagnementSaved(true);
    try { await api.post("/lead/investissement-immo", { message: "" }); } catch (_e) { /* silent */ }
  };

  return (
    <div className="mt-10 pt-8 border-t" style={{ borderColor: "rgba(21,41,79,0.1)" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 style={{ fontSize: 20 }}>Mes biens immobiliers ({biens.length})</h3>
        <button onClick={() => setAdding(true)} className="auprea-btn" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 14 }} data-testid="btn-add-bien">
          <Plus size={16} /> Ajouter un bien immobilier
        </button>
      </div>

      {biens.length === 0 && !adding && (
        <div
          style={{
            background: "rgba(202,168,106,0.08)",
            borderLeft: "3px solid var(--auprea-gold)",
            padding: "18px 22px",
            marginBottom: 16,
          }}
          data-testid="accompagnement-immo-block"
        >
          <div style={{ fontFamily: "Spectral, serif", fontSize: 17, color: "var(--auprea-navy)", marginBottom: 8 }}>
            Vous n&apos;avez pas encore de patrimoine immobilier
          </div>
          <p style={{ fontSize: 14, color: "rgba(35,38,46,0.75)", lineHeight: 1.6, marginBottom: 12 }}>
            Souhaitez-vous être accompagné(e) par l&apos;un de nos partenaires pour investir dans l&apos;immobilier ?
          </p>
          {accompagnementSaved ? (
            <div style={{ fontSize: 13, color: "var(--auprea-green)", fontStyle: "italic" }} data-testid="accompagnement-immo-confirm">
              ✓ Votre demande a bien été enregistrée. Un conseiller AUPREA vous recontactera sous 48h.
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={requestAccompagnement} className="auprea-btn" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 14 }} data-testid="btn-accompagnement-immo-oui">
                Oui, contactez-moi
              </button>
              <button onClick={() => setAccompagnementSaved(true)} className="auprea-btn-outline" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 14 }} data-testid="btn-accompagnement-immo-non">
                Non merci
              </button>
            </div>
          )}
        </div>
      )}

      {biens.map((b) => {
        const label = b.type_bien || b.type || "Bien";
        const addr = isFoncierBien(b)
          ? `${b.commune || ""}${b.numero_cadastre ? " · Cadastre : " + b.numero_cadastre : ""}`
          : (b.adresse_complete
              ? `${b.adresse_complete}${b.code_postal || b.ville ? " — " + [b.code_postal, b.ville].filter(Boolean).join(" ") : ""}`
              : (b.localisation || ""));
        return (
          <div key={b.id} className="py-4 border-b" style={{ borderColor: "rgba(21,41,79,0.08)" }} data-testid={`bien-row-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div style={{ fontSize: 12, color: "var(--auprea-gold)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 6 }}>
                  {label}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                  <div className="md:col-span-2" style={{ background: "rgba(243,239,230,0.6)", padding: "10px 14px", borderLeft: "3px solid var(--auprea-gold)" }}>
                    <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {isFoncierBien(b) ? "Cadastre / commune" : "Adresse complète"}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "var(--auprea-navy)" }}>{addr || "-"}</div>
                  </div>
                  <div style={{ background: "rgba(47,125,91,0.06)", padding: "10px 14px", borderLeft: "3px solid var(--auprea-green)" }}>
                    <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Valeur estimée</div>
                    <div style={{ fontFamily: "Spectral, serif", fontSize: 22, color: "var(--auprea-green)", fontWeight: 500 }}>
                      {b.valeur ? `${Number(b.valeur).toLocaleString("fr-FR")} €` : "—"}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" style={{ color: "rgba(35,38,46,0.7)" }}>
                  <div><span style={{ fontSize: 11, textTransform: "uppercase" }}>Détention</span> : {b.detention || "-"}</div>
                  <div><span style={{ fontSize: 11, textTransform: "uppercase" }}>Crédit</span> : {(b.credit_en_cours || "").toLowerCase().startsWith("oui") ? (b.credit_montant_restant ? `${Number(b.credit_montant_restant).toLocaleString("fr-FR")} € restant` : "Oui") : "Non"}</div>
                  {b.type_bail && <div><span style={{ fontSize: 11, textTransform: "uppercase" }}>Bail</span> : {b.type_bail}</div>}
                  {b.remarque_locataire && <div style={{ fontStyle: "italic" }}>{b.remarque_locataire}</div>}
                </div>
              </div>
              <button onClick={() => del(b.id)} style={{ color: "#b53a3a" }} data-testid={`btn-del-bien-${b.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        );
      })}

      {adding && (
        <div className="mt-4 p-4" style={{ background: "rgba(243,239,230,0.4)", border: "1px solid rgba(21,41,79,0.1)" }}>
          {/* Étape 1 : type de bien */}
          <div className="mb-3">
            <label style={{ fontSize: 12, color: "var(--auprea-navy)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>De quel type de bien s&apos;agit-il ? *</label>
            <select className="auprea-input" value={form.type_bien} onChange={(e) => setForm({ ...form, type_bien: e.target.value })} data-testid="input-bien-type_bien">
              <option value="">— Choisir —</option>
              <option value="Résidence principale">Résidence principale</option>
              <option value="Résidence secondaire">Résidence secondaire</option>
              <option value="Investissement locatif">Investissement locatif</option>
              <option value="Foncier / Terrain">Foncier / Terrain</option>
            </select>
          </div>

          {form.type_bien && (
            <>
              {/* Champs spécifiques Foncier / Terrain */}
              {isFoncier ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Numéro de cadastre ou de lot</label>
                    <input className="auprea-input" placeholder="Ex. AB 123" value={form.numero_cadastre} onChange={(e) => setForm({ ...form, numero_cadastre: e.target.value })} data-testid="input-bien-cadastre" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Commune / localisation</label>
                    <input className="auprea-input" placeholder="Ex. Sainte-Anne (971)" value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })} data-testid="input-bien-commune" />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div className="md:col-span-2">
                    <label style={{ fontSize: 12, color: "var(--auprea-navy)", textTransform: "uppercase", fontWeight: 600 }}>Adresse du bien *</label>
                    <input className="auprea-input" placeholder="N°, rue / lieu-dit" value={form.adresse_complete} onChange={(e) => setForm({ ...form, adresse_complete: e.target.value })} data-testid="input-bien-adresse" />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Code postal</label>
                    <input className="auprea-input" placeholder="75011" value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} data-testid="input-bien-cp" />
                  </div>
                  <div className="md:col-span-3">
                    <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Ville</label>
                    <input className="auprea-input" placeholder="Paris" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} data-testid="input-bien-ville" />
                  </div>
                </div>
              )}

              {/* Champs communs : valeur + détention + crédit */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label style={{ fontSize: 12, color: "var(--auprea-green)", textTransform: "uppercase", fontWeight: 600 }}>Valeur estimée (€) *</label>
                  <input type="number" className="auprea-input" placeholder="250000" value={form.valeur} onChange={(e) => setForm({ ...form, valeur: e.target.value })} data-testid="input-bien-valeur" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Mode de détention</label>
                  <select className="auprea-input" value={form.detention} onChange={(e) => setForm({ ...form, detention: e.target.value })} data-testid="input-bien-detention">
                    <option value="">— Choisir —</option>
                    <option value="Pleine propriété">Pleine propriété</option>
                    <option value="Indivision">Indivision</option>
                    <option value="SCI (société civile immobilière)">SCI (société civile immobilière)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Crédit immobilier en cours ?</label>
                  <select className="auprea-input" value={form.credit_en_cours} onChange={(e) => setForm({ ...form, credit_en_cours: e.target.value })} data-testid="input-bien-credit">
                    <option value="">— Choisir —</option>
                    <option value="Oui">Oui</option>
                    <option value="Non">Non</option>
                  </select>
                </div>
                {hasCredit && (
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Montant restant dû (€)</label>
                    <input type="number" className="auprea-input" placeholder="120000" value={form.credit_montant_restant} onChange={(e) => setForm({ ...form, credit_montant_restant: e.target.value })} data-testid="input-bien-credit-montant" />
                  </div>
                )}
              </div>

              {/* Champs conditionnels : Investissement locatif */}
              {isLocatif && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Type de bail</label>
                    <select className="auprea-input" value={form.type_bail} onChange={(e) => setForm({ ...form, type_bail: e.target.value })} data-testid="input-bien-bail">
                      <option value="">— Choisir —</option>
                      <option value="Bail nu">Bail nu</option>
                      <option value="Bail meublé">Bail meublé</option>
                      <option value="Bail commercial">Bail commercial</option>
                      <option value="Bail mixte">Bail mixte</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Remarque locataire</label>
                    <input className="auprea-input" placeholder="Un arrangement particulier ? (facultatif)" value={form.remarque_locataire} onChange={(e) => setForm({ ...form, remarque_locataire: e.target.value })} data-testid="input-bien-remarque" />
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex gap-2 mt-4">
            <button onClick={add} disabled={!form.type_bien} className="auprea-btn" data-testid="btn-save-bien">
              <Save size={14} style={{ display: "inline", marginRight: 6 }} /> Enregistrer
            </button>
            <button onClick={() => { setForm(initForm); setAdding(false); }} className="auprea-btn-outline">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

function isFoncierBien(b) {
  return /foncier|terrain/i.test(b?.type_bien || b?.type || "");
}

function PersonnesList({ personnes, reload }) {
  const [adding, setAdding] = useState(false);
  const emptyForm = { nom: "", lien: "", email: "", telephone: "", roles: [], role_autre: "" };
  const [form, setForm] = useState(emptyForm);
  const [inviting, setInviting] = useState({});
  const [editRolesFor, setEditRolesFor] = useState(null);
  const [editRolesValue, setEditRolesValue] = useState({ roles: [], role_autre: "" });

  const toggleRoleInForm = (role) => {
    setForm((f) => {
      const has = f.roles.includes(role);
      return { ...f, roles: has ? f.roles.filter((r) => r !== role) : [...f.roles, role] };
    });
  };
  const toggleRoleInEdit = (role) => {
    setEditRolesValue((v) => {
      const has = v.roles.includes(role);
      return { ...v, roles: has ? v.roles.filter((r) => r !== role) : [...v.roles, role] };
    });
  };

  const add = async () => {
    if (!form.nom) return;
    const payload = {
      nom: form.nom,
      lien: form.lien,
      role: (form.roles && form.roles[0]) || "", // legacy compat
      roles: form.roles,
      role_autre: form.role_autre,
      email: form.email,
      telephone: form.telephone,
    };
    await api.post("/carnet/personnes", payload);
    setForm(emptyForm);
    setAdding(false);
    reload();
  };
  const del = async (id) => { await api.delete(`/carnet/personnes/${id}`); reload(); };
  const invite = async (id) => {
    setInviting((i) => ({ ...i, [id]: true }));
    try { await api.post(`/carnet/personnes/${id}/invite`); reload(); }
    catch (_e) { /* show toast */ }
    finally { setInviting((i) => ({ ...i, [id]: false })); }
  };

  const openEditRoles = (p) => {
    const existing = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : (p.role ? [p.role] : []);
    setEditRolesValue({ roles: existing, role_autre: p.role_autre || "" });
    setEditRolesFor(p.id);
  };
  const saveEditRoles = async (id) => {
    try {
      await api.patch(`/carnet/personnes/${id}/roles`, {
        roles: editRolesValue.roles,
        role_autre: editRolesValue.role_autre,
      });
      setEditRolesFor(null);
      reload();
    } catch (_e) { /* silent */ }
  };

  const rolesToDisplay = (p) => {
    const list = Array.isArray(p.roles) && p.roles.length > 0
      ? p.roles
      : (p.role ? [p.role] : []);
    return list;
  };

  return (
    <div className="mt-10 pt-8 border-t" style={{ borderColor: "rgba(21,41,79,0.1)" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 style={{ fontSize: 20 }}>Personnes de confiance ({personnes.length})</h3>
        <button onClick={() => setAdding(true)} className="auprea-btn" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 14 }} data-testid="btn-add-personne">
          <Plus size={16} /> Ajouter une personne de confiance
        </button>
      </div>

      {personnes.length === 0 && !adding && (
        <div
          style={{
            fontSize: 13,
            color: "rgba(35,38,46,0.55)",
            background: "rgba(35,38,46,0.03)",
            padding: "14px 16px",
            borderRadius: 3,
          }}
          data-testid="personnes-empty"
        >
          Aucune personne de confiance renseignée pour l&apos;instant.
        </div>
      )}

      {personnes.map((p) => {
        const roles = rolesToDisplay(p);
        const isEditing = editRolesFor === p.id;
        return (
          <div key={p.id} className="py-4 border-b" style={{ borderColor: "rgba(21,41,79,0.08)" }} data-testid={`personne-row-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span style={{ fontFamily: "Spectral, serif", fontSize: 17, color: "var(--auprea-navy)", fontWeight: 600 }}>{p.nom || "-"}</span>
                  {p.lien && <span style={{ fontSize: 12, color: "rgba(35,38,46,0.55)" }}>· {p.lien}</span>}
                </div>
                <div className="flex flex-wrap gap-2 mb-2" data-testid={`personne-badges-${p.id}`}>
                  {roles.length === 0 && (
                    <span style={{ fontSize: 12, color: "rgba(35,38,46,0.5)", fontStyle: "italic" }}>Aucun rôle assigné</span>
                  )}
                  {roles.map((r) => (
                    <span
                      key={r}
                      style={{
                        background: "rgba(202,168,106,0.15)",
                        color: "var(--auprea-navy)",
                        border: "1px solid rgba(202,168,106,0.4)",
                        padding: "3px 10px",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {r}
                    </span>
                  ))}
                  {p.role_autre && (
                    <span style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", fontStyle: "italic" }}>· {p.role_autre}</span>
                  )}
                </div>
                <div className="text-sm" style={{ color: "rgba(35,38,46,0.7)" }}>
                  {p.email && <span data-testid={`personne-email-${p.id}`}>{p.email}</span>}
                  {p.email && p.telephone && <span> · </span>}
                  {p.telephone && <span>{p.telephone}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => (isEditing ? setEditRolesFor(null) : openEditRoles(p))}
                    className="text-sm"
                    style={{ color: "var(--auprea-blue)" }}
                    data-testid={`btn-edit-roles-${p.id}`}
                  >
                    {isEditing ? "Fermer" : "Modifier rôles"}
                  </button>
                  {p.email && (
                    p.invite ? (
                      <span style={{ fontSize: 12, color: "var(--auprea-green)", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`badge-invited-${p.id}`}>
                        <CheckCircle2 size={14} /> Invité
                      </span>
                    ) : (
                      <button onClick={() => invite(p.id)} disabled={inviting[p.id]} className="text-sm" style={{ color: "var(--auprea-blue)" }} data-testid={`btn-invite-${p.id}`}>
                        <Mail size={14} style={{ display: "inline", marginRight: 4 }} />
                        {inviting[p.id] ? "Envoi…" : "Inviter"}
                      </button>
                    )
                  )}
                  <button onClick={() => del(p.id)} style={{ color: "#b53a3a" }} data-testid={`btn-del-personne-${p.id}`}><Trash2 size={16} /></button>
                </div>
              </div>
            </div>

            {isEditing && (
              <div className="mt-3 p-3" style={{ background: "rgba(243,239,230,0.5)", border: "1px solid rgba(21,41,79,0.1)" }} data-testid={`edit-roles-panel-${p.id}`}>
                <RolesCheckboxes
                  selected={editRolesValue.roles}
                  onToggle={toggleRoleInEdit}
                  roleAutre={editRolesValue.role_autre}
                  onRoleAutreChange={(v) => setEditRolesValue((s) => ({ ...s, role_autre: v }))}
                  testIdPrefix={`edit-role-${p.id}`}
                />
                <div className="flex gap-2 mt-3">
                  <button onClick={() => saveEditRoles(p.id)} className="auprea-btn" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 13 }} data-testid={`btn-save-roles-${p.id}`}>
                    Enregistrer les rôles
                  </button>
                  <button onClick={() => setEditRolesFor(null)} className="auprea-btn-outline" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 13 }}>Annuler</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {adding && (
        <div className="mt-4 p-4" style={{ background: "rgba(243,239,230,0.4)", border: "1px solid rgba(21,41,79,0.1)" }} data-testid="personne-add-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Nom complet *</label>
              <input className="auprea-input" placeholder="Prénom Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="input-personne-nom" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Lien avec vous</label>
              <input className="auprea-input" placeholder="Ex. frère, ami proche…" value={form.lien} onChange={(e) => setForm({ ...form, lien: e.target.value })} data-testid="input-personne-lien" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Email</label>
              <input type="email" className="auprea-input" placeholder="prenom@email.fr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-personne-email" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", textTransform: "uppercase" }}>Téléphone</label>
              <input className="auprea-input" placeholder="06 12 34 56 78" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="input-personne-tel" />
            </div>
          </div>

          <div className="mt-4">
            <label style={{ fontSize: 12, color: "var(--auprea-navy)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
              Rôle de cette personne <span style={{ fontWeight: 400, textTransform: "none", color: "rgba(35,38,46,0.55)" }}>· plusieurs choix possibles</span>
            </label>
            <RolesCheckboxes
              selected={form.roles}
              onToggle={toggleRoleInForm}
              roleAutre={form.role_autre}
              onRoleAutreChange={(v) => setForm((f) => ({ ...f, role_autre: v }))}
              testIdPrefix="new-role"
            />
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={add} disabled={!form.nom} className="auprea-btn" data-testid="btn-save-personne">
              <Save size={14} style={{ display: "inline", marginRight: 6 }} /> Enregistrer
            </button>
            <button onClick={() => { setForm(emptyForm); setAdding(false); }} className="auprea-btn-outline">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RolesCheckboxes({ selected, onToggle, roleAutre, onRoleAutreChange, testIdPrefix }) {
  const showAutrePrecision = selected.includes("Autre (préciser)");
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
        {ROLES_PERSONNE_CONFIANCE.map((r) => {
          const checked = selected.includes(r);
          const testId = `${testIdPrefix}-${r.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`;
          return (
            <label
              key={r}
              htmlFor={testId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                border: `1px solid ${checked ? "var(--auprea-gold)" : "rgba(21,41,79,0.15)"}`,
                background: checked ? "rgba(202,168,106,0.08)" : "#fff",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              <input
                id={testId}
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(r)}
                data-testid={testId}
                style={{ accentColor: "var(--auprea-gold)" }}
              />
              <span style={{ color: "var(--auprea-navy)" }}>{r}</span>
            </label>
          );
        })}
      </div>
      {showAutrePrecision && (
        <div className="mt-2">
          <input
            className="auprea-input"
            placeholder="Précisez le rôle…"
            value={roleAutre}
            onChange={(e) => onRoleAutreChange(e.target.value)}
            data-testid={`${testIdPrefix}-autre-precision`}
          />
        </div>
      )}
    </div>
  );
}
